"use client"

import type React from "react"
import { useState } from "react"
import { CreditCard, Clock, Lock, Users } from "lucide-react"

interface BenefitTab {
  id: number
  label: string
  icon: JSX.Element
  content: JSX.Element
}

const benefitTabs: BenefitTab[] = [
  {
    id: 1,
    label: "Seamless Access",
    icon: <CreditCard className="w-5 h-5" />,
    content: (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div className="flex flex-col gap-5">
          <span className="inline-block px-4 py-1.5 bg-[#e6fffa] text-[#008080] rounded-full text-sm font-medium font-roboto shadow-sm">
            Effortless Healthcare
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-800 font-nunito leading-tight">
            Seamless Healthcare Access
          </h3>
          <p className="text-gray-600 font-roboto text-lg">
            Access your health records and services anytime, anywhere with ease.
          </p>
          <ul className="flex flex-col gap-3 mt-2">
            {["Instant access to records", "Cross-provider integration", "User-friendly interface"].map(
              (item, index) => (
                <li
                  key={index}
                  className="flex items-center bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:translate-x-1"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#e6fffa] mr-3">
                    <svg className="w-5 h-5 text-[#008080]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="font-roboto font-medium">{item}</span>
                </li>
              ),
            )}
          </ul>
        </div>
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-[#99f6e4] to-[#5eead4] rounded-2xl blur-md opacity-30 transform rotate-2"></div>
          <div className="bg-white p-6 rounded-2xl shadow-lg transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl border border-gray-100 overflow-hidden">
            {/* Card header with icon */}
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#e6fffa] mr-3">
                <CreditCard className="w-5 h-5 text-[#008080]" />
              </div>
              <h4 className="text-xl font-semibold text-gray-800 font-nunito">Seamless Access</h4>
            </div>

            {/* Card content */}
            <p className="text-gray-600 font-roboto text-sm mb-6 leading-relaxed">
              Experience effortless healthcare with instant access to your records, seamless integration across
              providers, and an intuitive interface designed for ease of use.
            </p>

            {/* Card footer with decorative elements */}
            <div className="relative">
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-[#e6fffa] rounded-full opacity-20 transform translate-x-8 translate-y-8"></div>
              <div className="absolute bottom-4 right-4 w-12 h-12 bg-[#e6fffa] rounded-full opacity-40"></div>

              {/* Feature list */}
              <ul className="relative z-10 space-y-2">
                <li className="flex items-center text-xs text-gray-500">
                  <div className="w-4 h-4 rounded-full bg-[#e6fffa] flex items-center justify-center mr-2">
                    <div className="w-2 h-2 rounded-full bg-[#008080]"></div>
                  </div>
                  Instant access
                </li>
                <li className="flex items-center text-xs text-gray-500">
                  <div className="w-4 h-4 rounded-full bg-[#e6fffa] flex items-center justify-center mr-2">
                    <div className="w-2 h-2 rounded-full bg-[#008080]"></div>
                  </div>
                  User-friendly
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 2,
    label: "Time-Saving",
    icon: <Clock className="w-5 h-5" />,
    content: (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div className="flex flex-col gap-5">
          <span className="inline-block px-4 py-1.5 bg-[#e6fffa] text-[#008080] rounded-full text-sm font-medium font-roboto shadow-sm">
            Efficient Workflow
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-800 font-nunito leading-tight">
            Time-Saving Efficiency
          </h3>
          <p className="text-gray-600 font-roboto text-lg">Streamline your healthcare tasks to save valuable time.</p>
          <ul className="flex flex-col gap-3 mt-2">
            {["Quick appointment booking", "Automated reminders", "Centralized health management"].map(
              (item, index) => (
                <li
                  key={index}
                  className="flex items-center bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:translate-x-1"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#e6fffa] mr-3">
                    <svg className="w-5 h-5 text-[#008080]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="font-roboto font-medium">{item}</span>
                </li>
              ),
            )}
          </ul>
        </div>
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-[#99f6e4] to-[#5eead4] rounded-2xl blur-md opacity-30 transform -rotate-2"></div>
          <div className="bg-white p-6 rounded-2xl shadow-lg transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl border border-gray-100 overflow-hidden">
            {/* Card header with icon */}
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#e6fffa] mr-3">
                <Clock className="w-5 h-5 text-[#008080]" />
              </div>
              <h4 className="text-xl font-semibold text-gray-800 font-nunito">Time-Saving</h4>
            </div>

            {/* Card content */}
            <p className="text-gray-600 font-roboto text-sm mb-6 leading-relaxed">
              Save time with quick appointment booking, automated reminders, and a centralized platform to manage all
              your healthcare needs efficiently.
            </p>

            {/* Card footer with decorative elements */}
            <div className="relative">
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-[#e6fffa] rounded-full opacity-20 transform translate-x-8 translate-y-8"></div>
              <div className="absolute bottom-4 right-4 w-12 h-12 bg-[#e6fffa] rounded-full opacity-40"></div>

              {/* Feature list */}
              <ul className="relative z-10 space-y-2">
                <li className="flex items-center text-xs text-gray-500">
                  <div className="w-4 h-4 rounded-full bg-[#e6fffa] flex items-center justify-center mr-2">
                    <div className="w-2 h-2 rounded-full bg-[#008080]"></div>
                  </div>
                  Quick booking
                </li>
                <li className="flex items-center text-xs text-gray-500">
                  <div className="w-4 h-4 rounded-full bg-[#e6fffa] flex items-center justify-center mr-2">
                    <div className="w-2 h-2 rounded-full bg-[#008080]"></div>
                  </div>
                  Smart reminders
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 3,
    label: "Enhanced Security",
    icon: <Lock className="w-5 h-5" />,
    content: (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div className="flex flex-col gap-5">
          <span className="inline-block px-4 py-1.5 bg-[#e6fffa] text-[#008080] rounded-full text-sm font-medium font-roboto shadow-sm">
            Data Protection
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-800 font-nunito leading-tight">Enhanced Security</h3>
          <p className="text-gray-600 font-roboto text-lg">
            Your health data is safeguarded with top-tier security measures.
          </p>
          <ul className="flex flex-col gap-3 mt-2">
            {["End-to-end encryption", "Multi-factor authentication", "Compliance with regulations"].map(
              (item, index) => (
                <li
                  key={index}
                  className="flex items-center bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:translate-x-1"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#e6fffa] mr-3">
                    <svg className="w-5 h-5 text-[#008080]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="font-roboto font-medium">{item}</span>
                </li>
              ),
            )}
          </ul>
        </div>
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-[#99f6e4] to-[#5eead4] rounded-2xl blur-md opacity-30 transform rotate-2"></div>
          <div className="bg-white p-6 rounded-2xl shadow-lg transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl border border-gray-100 overflow-hidden">
            {/* Card header with icon */}
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#e6fffa] mr-3">
                <Lock className="w-5 h-5 text-[#008080]" />
              </div>
              <h4 className="text-xl font-semibold text-gray-800 font-nunito">Enhanced Security</h4>
            </div>

            {/* Card content */}
            <p className="text-gray-600 font-roboto text-sm mb-6 leading-relaxed">
              Protect your health data with end-to-end encryption, multi-factor authentication, and strict compliance
              with industry regulations.
            </p>

            {/* Card footer with decorative elements */}
            <div className="relative">
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-[#e6fffa] rounded-full opacity-20 transform translate-x-8 translate-y-8"></div>
              <div className="absolute bottom-4 right-4 w-12 h-12 bg-[#e6fffa] rounded-full opacity-40"></div>

              {/* Feature list */}
              <ul className="relative z-10 space-y-2">
                <li className="flex items-center text-xs text-gray-500">
                  <div className="w-4 h-4 rounded-full bg-[#e6fffa] flex items-center justify-center mr-2">
                    <div className="w-2 h-2 rounded-full bg-[#008080]"></div>
                  </div>
                  End-to-end encryption
                </li>
                <li className="flex items-center text-xs text-gray-500">
                  <div className="w-4 h-4 rounded-full bg-[#e6fffa] flex items-center justify-center mr-2">
                    <div className="w-2 h-2 rounded-full bg-[#008080]"></div>
                  </div>
                  Multi-factor auth
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 4,
    label: "Family Care",
    icon: <Users className="w-5 h-5" />,
    content: (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div className="flex flex-col gap-5">
          <span className="inline-block px-4 py-1.5 bg-[#e6fffa] text-[#008080] rounded-full text-sm font-medium font-roboto shadow-sm">
            Family Health
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-800 font-nunito leading-tight">
            Family-Centric Care
          </h3>
          <p className="text-gray-600 font-roboto text-lg">Manage healthcare for your entire family in one place.</p>
          <ul className="flex flex-col gap-3 mt-2">
            {["Multiple profiles", "Custom access controls", "Child health tracking"].map((item, index) => (
              <li
                key={index}
                className="flex items-center bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:translate-x-1"
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#e6fffa] mr-3">
                  <svg className="w-5 h-5 text-[#008080]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="font-roboto font-medium">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-[#99f6e4] to-[#5eead4] rounded-2xl blur-md opacity-30 transform -rotate-2"></div>
          <div className="bg-white p-6 rounded-2xl shadow-lg transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl border border-gray-100 overflow-hidden">
            {/* Card header with icon */}
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#e6fffa] mr-3">
                <Users className="w-5 h-5 text-[#008080]" />
              </div>
              <h4 className="text-xl font-semibold text-gray-800 font-nunito">Family Care</h4>
            </div>

            {/* Card content */}
            <p className="text-gray-600 font-roboto text-sm mb-6 leading-relaxed">
              Simplify family healthcare with multiple profiles, custom access controls, and dedicated tools for
              tracking your children's health.
            </p>

            {/* Card footer with decorative elements */}
            <div className="relative">
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-[#e6fffa] rounded-full opacity-20 transform translate-x-8 translate-y-8"></div>
              <div className="absolute bottom-4 right-4 w-12 h-12 bg-[#e6fffa] rounded-full opacity-40"></div>

              {/* Feature list */}
              <ul className="relative z-10 space-y-2">
                <li className="flex items-center text-xs text-gray-500">
                  <div className="w-4 h-4 rounded-full bg-[#e6fffa] flex items-center justify-center mr-2">
                    <div className="w-2 h-2 rounded-full bg-[#008080]"></div>
                  </div>
                  Multiple profiles
                </li>
                <li className="flex items-center text-xs text-gray-500">
                  <div className="w-4 h-4 rounded-full bg-[#e6fffa] flex items-center justify-center mr-2">
                    <div className="w-2 h-2 rounded-full bg-[#008080]"></div>
                  </div>
                  Child health tracking
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    ),
  },
]

const BenefitsTabs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<number>(1)

  return (
    <section id="benefits" className="py-16 md:py-24 bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
          <span className="inline-block px-5 py-2 bg-gradient-to-r from-[#e6fffa] to-[#b2f5ea] text-[#008080] rounded-full text-sm font-medium font-roboto mb-6 shadow-lg border border-[#99f6e4] hover:shadow-xl hover:-translate-y-0.5 transform transition-all duration-300">
              Why Choose Us
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#008080] mb-6 font-nunito leading-tight">
              The Benefits for You
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#008080] to-[#00a3a3] mx-auto rounded-full mb-6"></div>
          </div>

          {/* Tabs */}
          <div className="flex justify-center flex-wrap gap-3 mb-12">
            {benefitTabs.map((tab) => (
              <button
                key={tab.id}
                className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium font-roboto transition-all duration-300 ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-[#008080] to-[#00a3a3] text-white shadow-md scale-105"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:shadow-sm"
                }`}
                onClick={() => setActiveTab(tab.id)}
                aria-selected={activeTab === tab.id}
                aria-controls={`benefit-tab-${tab.id}`}
                role="tab"
              >
                <span
                  className={`${
                    activeTab === tab.id ? "bg-white bg-opacity-20" : "bg-[#e6fffa]"
                  } p-1.5 rounded-full flex items-center justify-center`}
                >
                  {tab.icon}
                </span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-gray-100">
            {benefitTabs.map((tab) => (
              <div
                key={tab.id}
                id={`benefit-tab-${tab.id}`}
                className={`transition-all duration-500 ${
                  activeTab === tab.id
                    ? "block opacity-100 transform translate-y-0"
                    : "hidden opacity-0 transform translate-y-4"
                }`}
                role="tabpanel"
                aria-labelledby={`tab-${tab.id}`}
              >
                {tab.content}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default BenefitsTabs